import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCertificationAssessment } from '../../services/apiService';

const Certificationoverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { selectedChangeIds, productType } = location.state || {};
  
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedChangeIds || selectedChangeIds.length === 0) {
      setError('No changes selected');
      setLoading(false);
      return;
    }

    fetchCertificationData();
  }, [selectedChangeIds, productType]);

  const fetchCertificationData = async () => {
    try {
      setLoading(true);
      const response = await getCertificationAssessment(productType, selectedChangeIds);
      
      if (response.success) {
        setCertifications(response.data.certifications);
      } else {
        setError(response.message || 'Failed to load certification data');
      }
    } catch (err) {
      setError('Error loading certification data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMoreDetails = () => {
    navigate('/certification-details', {
      state: {
        certifications,
        selectedChangeIds,
        productType
      }
    });
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading certification assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={handleBackClick}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
        <div className="flex items-center px-6 py-4">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center w-12 h-12 -ml-2 mr-3 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
          </button>
          <h1 className="text-2xl font-semibold text-white">
            HW Change Verification Assessment for Certification
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        <div className="mb-6">
          <p className="text-gray-400 text-sm">
            For an overview of certification requirements evaluated based on HW change content, please refer to the table below.
          </p>
          <p className="text-gray-400 text-sm mt-2">
            If you want to view more detailed information, please click the "More Details" button.
          </p>
        </div>

        {/* More Details Button */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={handleMoreDetails}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg text-white font-medium transition-colors text-sm"
          >
            More Details
          </button>
        </div>

        {/* Certification Table */}
        <div className="bg-gray-900/40 rounded-xl border border-gray-700 overflow-hidden shadow-2xl">
          {/* Table Header */}
          <div className="grid grid-cols-[2fr_1fr_1fr] bg-gradient-to-r from-gray-800/80 to-gray-700/80 border-b border-gray-600">
            <div className="px-6 py-4 text-base font-semibold text-gray-100 border-r border-gray-600">
              Logo Certification
            </div>
            <div className="px-6 py-4 text-base font-semibold text-gray-100 border-r border-gray-600 text-center">
              Is certification required
            </div>
            <div className="px-6 py-4 text-base font-semibold text-gray-100 text-center">
              Owner
            </div>
          </div>

          {/* Table Body */}
          {certifications.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No certification requirements found for selected changes</p>
            </div>
          ) : (
            certifications.map((cert, index) => {
              const isCertRequired = cert.isCertificationRequired;
              const owner = cert.owner || 'N/A';
              
              return (
                <div 
                  key={index}
                  className="grid grid-cols-[2fr_1fr_1fr] border-b border-gray-700/50 last:border-b-0 hover:bg-gray-800/30 transition-colors"
                >
                  <div className="px-6 py-4 text-sm text-white border-r border-gray-700/50">
                    {cert.certificationName}
                  </div>
                  <div className="px-6 py-4 text-sm text-white border-r border-gray-700/50 text-center font-semibold">
                    {isCertRequired}
                  </div>
                  <div className="px-6 py-4 text-sm text-gray-300 text-center">
                    {owner}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Certificationoverview;